import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, lastValueFrom } from 'rxjs';
import { Papertypes } from 'src/app/models/papertypes';
import { LoggingService } from 'src/app/services/logging.service';
import { OrganisationApiClient } from 'src/app/services/organisation.apiclient';
import { ToastService } from 'src/app/services/toast.service';
import { SettingsService } from '../../settings.service';
import { SettingsBreadCrumbService, BreadCrumbOptions } from '../../settings-breadcrumb.service';
import { IdsForSettingsService } from '../../ids-for-settings.service';
import { IdentityOrganisationApiClient } from 'src/app/services/api-clients/identity-organisation.api-client';
import { SafeUrl } from '@angular/platform-browser';
import { OrganisationSettingsApiClient } from 'src/app/services/organisation-settings.apiclient';
import { DefaultAllowedSafeTransferExtensions, PlatformSettings } from 'src/app/constants/settings.enum';
import { TagInputComponent } from 'ngx-chips';

@Component({
  templateUrl: './documents.page.html'
})
export class DocumentsPage implements OnDestroy {

  allowedExtensionsString: string[] = [];
  defaultExtensionsString: string[] = DefaultAllowedSafeTransferExtensions;

  @ViewChild('extensionTagInput') tagInput!: TagInputComponent;
  papertypes: Papertypes[] = [];
  translations: any = <any>{};
  previewBackground: SafeUrl = "../assets/blank_document.jpg";
  uploadedStationery!: any;
  stationeryName: string | null = null;
  stationeryUploaded = false;
  orderStationery: Papertypes[] = [];
  organisationIdSubscription: Subscription;
  selectedOrganisationId: string = "";
  organisation!: any;

  @Input('action') triggerAction!: any;

  constructor(private organisationApiClient: OrganisationApiClient, private translateService: TranslateService, private toastService: ToastService, private organisationSettingsApiClient: OrganisationSettingsApiClient,
    private loggingService: LoggingService, private settingsService: SettingsService, private settingsBreadCrumbService: SettingsBreadCrumbService, private idsForSettingsService: IdsForSettingsService,
    private identityOrganisationApiClient: IdentityOrganisationApiClient) {
    this.organisationIdSubscription = this.idsForSettingsService.selectedOrganisationId$.subscribe(organisationId => {
      if (organisationId === this.selectedOrganisationId) return;
      this.selectedOrganisationId = organisationId;
      // setup specialised breadcrumb
      this.settingsBreadCrumbService.SetOrganisationBasedCrumbs(BreadCrumbOptions.Application(this.selectedOrganisationId), BreadCrumbOptions.Application_Documents(this.selectedOrganisationId));
      this.getAllowedExtensions();
      this.getStationery();
    });

    this.getTranslations();
    translateService.onLangChange.subscribe(() => {
      this.getTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.organisationIdSubscription) {
      this.organisationIdSubscription.unsubscribe();
    }
  }

  getTranslations() {
    this.translateService.get([
      'Toasts.ErrorAddingStationery',
      'Toasts.ErrorRemovingStationery',
      'Toasts.ErrorMovingStationery',
      'Toasts.Error'
    ]).subscribe(translation => {
      this.translations.toastsErrorAddingStationery = translation['Toasts.ErrorAddingStationery'];
      this.translations.toastsErrorRemovingStationery = translation['Toasts.ErrorRemovingStationery'];
      this.translations.toastsErrorMovingStationery = translation['Toasts.ErrorMovingStationery'];
      this.translations.error = translation['Toasts.Error'];
    });
  }

  private getStationery(paperTypeId?: string) {
    this.papertypes = this.settingsService.getStationery(this.selectedOrganisationId);
    if (paperTypeId != null || paperTypeId !== undefined) {
      let paperTypeIndex = this.papertypes.findIndex(s => s.id === paperTypeId)
      if (paperTypeIndex > -1) this.previewBackground = this.papertypes[paperTypeIndex].thumbnail!;
    }
  }

  TriggerAction(event: any) {
    this.previewBackground = event;
    if (this.previewBackground == null) {
      this.previewBackground = "../assets/blank_document.jpg";
    }
  }

  selectStationery(event: any) {
    event.preventDefault();
    event.stopPropagation();
    let stationery = event.target.files[0];
    if ((stationery.name.substring(stationery.name.lastIndexOf(".")).toLowerCase()).replace('.', '') !== "pdf") return;
    this.uploadedStationery = stationery;
    if (this.stationeryName === null || this.stationeryName === undefined || this.stationeryName === "") this.stationeryName = this.uploadedStationery.name;
    this.stationeryUploaded = true;
  }

  changeStationeryName(event: any) {
    this.stationeryName = event.srcElement.value;
  }

  async addStationery() {
    if ((this.uploadedStationery && this.stationeryName)) {
      const uploadData = new FormData();
      uploadData.append('name', this.stationeryName);
      uploadData.append('background', this.uploadedStationery, this.uploadedStationery.name);
      let setPapertypes = this.organisationApiClient.setPapertypes(this.selectedOrganisationId, uploadData);
      lastValueFrom(setPapertypes).then((data: any) => {
        this.getStationery(data.id);
        this.stationeryName = null;
        this.uploadedStationery = null;
        this.stationeryUploaded = false;
      })
        .catch(() => {
          this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsErrorAddingStationery + "");
          this.stationeryUploaded = true;
        });
    }
  }

  async removeStationery(event: any) {
    this.organisationApiClient.removePapertype(this.selectedOrganisationId, event).subscribe({
      next: () => {
        let papertypeIndex = this.papertypes.findIndex(s => s.id === event)
        this.papertypes.splice(papertypeIndex, 1);
        this.previewBackground = "../assets/blank_document.jpg";
      },
      error: () => {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsErrorRemovingStationery + "");
      }
    });
  }

  papertypeChangeOrder(event: any) {
    moveItemInArray(this.papertypes, event.previousIndex, event.currentIndex);
    this.changeOrderStationery().catch((error) => this.loggingService.logException(error));
  }


  async changeOrderStationery() {
    (await this.organisationApiClient.changePapertypeOrder(this.selectedOrganisationId, this.papertypes)).subscribe({
      error: () => {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsErrorMovingStationery + "");
      },
      next: () => { }
    });
  }

  removeSelected() {
    let selectedStationery = <HTMLInputElement>document.getElementById('selectedStationery');
    selectedStationery.value = "";
  }
  newAllowedExtensionsPushed(additionalAllowedExtensions: string[]) {
    let setting = [{ key: PlatformSettings.AllowedDocumentExtensions, value: JSON.stringify(additionalAllowedExtensions) }];
    this.organisationSettingsApiClient.postOrganisationSettings(this.selectedOrganisationId, setting).subscribe();
    this.toastService.showSaved();
  }
  private getAllowedExtensions() {
    this.organisationApiClient.getAdditionalSafeTransferDocumentExtensions(this.selectedOrganisationId).subscribe((setting: string[]) => {
      this.allowedExtensionsString = setting;
    });
  }
}
